import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2 } from 'lucide-react';
import { PILLAR_PAGES } from '../data/pillarPages';
import { useSEO } from '../hooks/useSEO';

export const PillarPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const data = PILLAR_PAGES[slug];

  useSEO(data?.seoKey || 'home');

  if (!data) return <Navigate to="/" replace />;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Startseite', item: 'https://www.livag.ch/' },
      { '@type': 'ListItem', position: 2, name: data.h1, item: `https://www.livag.ch/${slug}` },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`pillar-${slug}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      {/* Hero */}
      <section className="bg-[#264F77] text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-white/70 mb-4">
            <Link to="/" className="hover:text-white">Startseite</Link>
            <span className="mx-2">/</span>
            <span>{data.h1}</span>
          </nav>
          <div className={`grid gap-10 items-center ${data.image ? 'lg:grid-cols-[3fr_2fr]' : ''}`}>
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-normal mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {data.h1}
              </h1>
              <p className="text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
                {data.lead}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  data-testid="pillar-cta-primary"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#264F77] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Jetzt kostenlose Erstberatung anfragen <ArrowRight size={18} />
                </Link>
                <a
                  href="tel:0712261199"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/30"
                >
                  <Phone size={18} /> 071 226 11 99
                </a>
              </div>
            </div>
            {data.image && (
              <div className="relative">
                <img
                  src={data.image}
                  alt={data.imageAlt || data.h1}
                  loading="lazy"
                  width="480"
                  height="600"
                  className="w-full max-w-sm mx-auto h-auto object-contain drop-shadow-2xl"
                />
                <figcaption className="mt-4 text-center text-white/90 text-sm md:text-base leading-relaxed" data-testid="pillar-hero-caption">
                  <div className="font-semibold text-white">Christoph Jermann</div>
                  <div>Immobilienökonom MAS FHO</div>
                  <div>eidg. Dipl. Betriebswirtschafter</div>
                </figcaption>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {data.sections.map((sec, i) => (
            <article key={i}>
              <h2 className="text-2xl md:text-3xl font-normal text-[#264F77] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                {sec.h2}
              </h2>
              {sec.body?.map((p, j) => (
                <p key={j} className="text-gray-700 leading-relaxed mb-4">{p}</p>
              ))}
              {sec.table && (
                <div className="overflow-x-auto mt-6">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b-2 border-[#264F77]">
                        {sec.table.head.map((h, k) => (
                          <th key={k} className="text-left px-4 py-3 font-semibold text-[#264F77]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sec.table.rows.map((row, ri) => (
                        <tr key={ri} className="border-b border-gray-100 hover:bg-gray-50">
                          {row.map((cell, ci) => (
                            <td key={ci} className="px-4 py-3 text-gray-700 align-top">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="text-3xl font-normal text-[#264F77] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Häufige Fragen
          </h2>
          <div className="space-y-4">
            {data.faqs.map((f, i) => (
              <details key={i} className="bg-white rounded-lg p-5 shadow-sm border border-gray-100 group">
                <summary className="cursor-pointer font-semibold text-[#264F77] flex items-start gap-3">
                  <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0 text-[#264F77]" />
                  <h3 className="inline text-base m-0">{f.q}</h3>
                </summary>
                <p className="mt-3 text-gray-700 leading-relaxed pl-8">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related & CTA */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-normal text-[#264F77] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Weiterführende Seiten
            </h2>
            <ul className="space-y-2">
              {data.related.map((r, i) => (
                <li key={i}>
                  <Link to={r.to} className="text-[#264F77] hover:underline inline-flex items-center gap-2">
                    <ArrowRight size={16} /> {r.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#264F77] text-white rounded-2xl p-8">
            <h2 className="text-xl font-semibold mb-2">Persönliche Beratung</h2>
            <p className="text-white/80 mb-4">Sprechen Sie direkt mit unserem Team – kostenlos und unverbindlich.</p>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2"><Phone size={16} /> <a href="tel:0712261199" className="hover:underline">071 226 11 99</a></p>
              <p className="flex items-center gap-2"><Mail size={16} /> <a href="mailto:info@livag.ch" className="hover:underline">info@livag.ch</a></p>
              <p className="flex items-start gap-2"><MapPin size={16} className="mt-0.5" /> Katharinengasse 4, 9000 St. Gallen</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PillarPage;
